/**
 * Audios — the "what was recorded for this patient?" viewer.
 *
 * Opened from the 🎧 Audios button that sits next to Restore in
 * PatientHeaderBar. Lists every recording attached to this chart PLUS the same
 * patient's other visits in this schema, and plays the selected one next to
 * whatever transcript(s) exist for it.
 *
 * Deliberately read-only: rename / delete / backup-bucket recovery stay in the
 * much heavier AudioManagerModal (Tools ▸ Audio Manager), which the footer
 * links to so this modal isn't a dead end when someone needs to manage a file.
 *
 * Rendered INLINE by MedStudentClinicPage — never portalled to document.body.
 * Tailwind utilities are scoped to `#clinic-root`, so a portalled copy would
 * render unstyled (see the Process-modal regression).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AudioPlayer from '../../components/table/audio/AudioPlayer';
import { pickAudioDisplayName } from '../../components/table/audio/AudioPlayerModal';
import {
  audioEntryVisitDate,
  deduplicateBySessionId,
  transcriptBelongsToAudio,
  transcriptShowsAsLive,
} from '../../utils/audioMetadata';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { tableName } from '../../components/env';

// How many other charts for this patient we pull audios from. Generous enough
// to cover a normal patient history; bounded so a decade-long chart can't turn
// opening the modal into a multi-megabyte fetch.
const OTHER_CHART_LIMIT = 30;

const toMillis = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// When a recording happened. `more_audios` rows are written by several
// different code paths over the years, so no single field is reliable.
export const audioMillis = (a) =>
  toMillis(a?.created_at) ||
  toMillis(a?.uploaded_at) ||
  toMillis(a?.recording_date) ||
  toMillis(a?.timestamp) ||
  toMillis(a?.metadata?.created_at) ||
  0;

const formatWhen = (ms) => (ms ? new Date(ms).toLocaleString() : '');

const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return '';
  const m = Math.floor(s / 60);
  const rest = Math.round(s % 60);
  return `${m}:${String(rest).padStart(2, '0')}`;
};

/**
 * Flatten a transcript row to plain text. `transcript` is a string on some
 * rows and an array of caption segments on others (live dictation), and the
 * segment shape varies too — hence the per-line fallbacks.
 */
export function transcriptToText(t) {
  if (!t) return '';
  const raw = t.transcript ?? t.transcript_text ?? '';
  if (Array.isArray(raw)) {
    return raw
      .map((line) => (typeof line === 'string' ? line : line?.transcript || line?.text || ''))
      .filter(Boolean)
      .join('\n');
  }
  return String(raw || '');
}

export const UNDATED = '__undated__';
// Which row an audio came from, carried through deduplication so the player
// can still be handed the right patient id. Prefixed so it can't collide with
// a real `more_audios` field.
const ROW_TAG = '__vmaiRowId';

/**
 * Group every recording we know about by the VISIT DAY it belongs to.
 *
 * This is the whole point of the module: `more_audios` is NOT per-visit. A
 * patient row accumulates recordings across dates, which is why every other
 * visit-scoped view in the app (MedStudentRightPanel, SchedulesTable,
 * ResultsTable, PatientPage) runs the column through filterAudiosForVisitDate()
 * before showing it. Grouping by the same rule — `audioEntryVisitDate`, storage
 * path date first, entry timestamps second — is what stops a March recording
 * from appearing under an August chart as if it were part of that visit.
 *
 * `sources` is [{ rowId, audios, transcripts }]; they are merged and
 * deduplicated GLOBALLY, because the same recording is frequently written onto
 * more than one of the patient's rows.
 *
 * Within a day, entries are ordered oldest-first so the numbering reads the way
 * the visit ran, and ALL matching transcripts are attached rather than just the
 * first — a recording can carry both the live caption stream and the Corti
 * post-processed pass.
 */
export function buildAudioGroups(sources, visitDate) {
  const tagged = [];
  const transcripts = [];
  for (const src of sources || []) {
    for (const a of src.audios || []) tagged.push({ ...a, [ROW_TAG]: src.rowId ?? null });
    for (const t of src.transcripts || []) transcripts.push(t);
  }

  const byDate = new Map();
  for (const audio of deduplicateBySessionId(tagged)) {
    const date = audioEntryVisitDate(audio) || UNDATED;
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date).push(audio);
  }

  const visit = visitDate ? String(visitDate).slice(0, 10) : null;
  const dates = [...byDate.keys()].sort((a, b) => {
    if (a === b) return 0;
    if (a === UNDATED) return 1; // undated always last
    if (b === UNDATED) return -1;
    if (visit && a === visit) return -1; // this visit always first
    if (visit && b === visit) return 1;
    return b.localeCompare(a); // then newest day first
  });

  return dates.map((date) => {
    const isThisVisit = Boolean(visit) && date === visit;
    const label = date === UNDATED ? 'Date unknown' : date;
    const ordered = [...byDate.get(date)].sort((a, b) => audioMillis(a) - audioMillis(b));
    return {
      key: date,
      label,
      isThisVisit,
      entries: ordered.map((audio, i) => ({
        id: `${date}::${audio.url || audio.session_id || i}`,
        audio,
        number: i + 1,
        rowId: audio[ROW_TAG] || null,
        groupLabel: label,
        isThisVisit,
        transcripts: transcripts.filter((t) => transcriptBelongsToAudio(audio, t)),
      })),
    };
  });
}

function TranscriptBlock({ transcript, index, total }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const text = transcriptToText(transcript);
  const kind = transcriptShowsAsLive(transcript) ? 'Live' : 'Corti';
  const when = formatWhen(toMillis(transcript?.created_at));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure origin / denied) — the text is on screen anyway */
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800/60 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/40">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300">
            {kind}
          </span>
          {total > 1 && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              transcript {index + 1} of {total}
            </span>
          )}
          {when && <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{when}</span>}
          {Array.isArray(transcript?.facts) && transcript.facts.length > 0 && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300">
              {transcript.facts.length} facts
            </span>
          )}
        </div>
        {text && (
          <button
            type="button"
            onClick={copy}
            className="text-[11px] font-medium px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800/70 shrink-0"
          >
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        )}
      </div>
      <div className="px-3 py-2 max-h-[38vh] overflow-y-auto">
        {text ? (
          <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
            {text}
          </p>
        ) : (
          <p className="text-xs italic text-slate-500 dark:text-slate-400">
            This transcript row has no text.
          </p>
        )}
      </div>
    </div>
  );
}

export default function ClinicAudiosModal({
  isOpen,
  onClose,
  patient,
  schema = null,
  // 'light' | 'dark' — the clinic page's effectiveTheme. Passed through to
  // AudioPlayer, whose error panel is otherwise a wall of near-white Chakra
  // tints that glare on the dark modal.
  theme = 'light',
  // Hands off to the full Audio Manager (backups, rename, delete). Optional —
  // no link is rendered without it.
  onOpenAudioManager = null,
}) {
  // OFF by default, and that default is the whole point: a chart shows ITS
  // visit's recordings. Pulling in the patient's other days made a March
  // recording show up on an August chart, which reads as a filing bug even
  // though the grouping was right. It stays reachable — some questions really
  // are "did we ever record this patient?" — but only when explicitly asked
  // for, and the query below doesn't even run until then.
  const [includeOtherDays, setIncludeOtherDays] = useState(false);
  const [otherCharts, setOtherCharts] = useState([]);
  const [loadingOthers, setLoadingOthers] = useState(false);
  const [othersError, setOthersError] = useState(null);
  // True when the visit query came back full — older visits exist that we
  // didn't look at. Said out loud rather than silently truncating, so nobody
  // reads an empty tail as "there's nothing older".
  const [othersCapped, setOthersCapped] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const effectiveSchema = schema || supabaseSchema;
  const patientId = patient?.id ? String(patient.id) : null;
  const ehrId = patient?.EHR_ID || '';
  const patientName = patient?.patient_name || '';

  // The chart's own scheduled day — the one `filterAudiosForVisitDate` would
  // filter by everywhere else. Null on roster charts, which have no visit of
  // their own, so nothing gets labelled "this visit" there.
  const visitDate = patient?.date_ || null;

  // ── this chart ───────────────────────────────────────────────────────────
  const currentChart = useMemo(
    () => ({
      rowId: patient?.id || null,
      audios: Array.isArray(patient?.more_audios) ? patient.more_audios : [],
      transcripts: Array.isArray(patient?.transcripts) ? patient.transcripts : [],
    }),
    [patient],
  );

  // ── the patient's other visits ───────────────────────────────────────────
  // Identity rule is copied verbatim from findPriorPopulatedVisit (Copy from
  // older): EHR_ID inside the chart's schema, falling back to patient_name.
  // Anything else and we don't search at all rather than risk listing another
  // patient's recordings.
  useEffect(() => {
    if (!isOpen || !includeOtherDays) return undefined;
    let cancelled = false;
    (async () => {
      setOthersError(null);
      setOthersCapped(false);
      if (!ehrId && !patientName) {
        setOtherCharts([]);
        return;
      }
      setLoadingOthers(true);
      try {
        const sb = await getSupabase();
        let query = sb
          .schema(effectiveSchema)
          .from(tableName)
          .select('id, EHR_ID, patient_name, date_, type, more_audios, transcripts')
          .order('date_', { ascending: false })
          .limit(OTHER_CHART_LIMIT);
        query = ehrId ? query.eq('EHR_ID', ehrId) : query.eq('patient_name', patientName);
        const { data, error } = await query;
        if (cancelled) return;
        if (error) {
          setOthersError(error.message || 'Could not load this patient’s other visits.');
          setOtherCharts([]);
          return;
        }
        setOthersCapped((data || []).length >= OTHER_CHART_LIMIT);
        const rows = (data || [])
          .filter((r) => String(r.id) !== patientId)
          .filter((r) => Array.isArray(r.more_audios) && r.more_audios.length > 0)
          .map((r) => ({
            rowId: r.id,
            audios: r.more_audios,
            transcripts: Array.isArray(r.transcripts) ? r.transcripts : [],
          }));
        setOtherCharts(rows);
      } catch (err) {
        if (!cancelled) {
          setOthersError(err?.message || 'Could not load this patient’s other visits.');
          setOtherCharts([]);
        }
      } finally {
        if (!cancelled) setLoadingOthers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, includeOtherDays, effectiveSchema, patientId, ehrId, patientName]);

  const allGroups = useMemo(
    () => buildAudioGroups([currentChart, ...otherCharts], visitDate),
    [currentChart, otherCharts, visitDate],
  );

  // What the list actually renders. With a visit date and the toggle off, that
  // is this visit's day and nothing else — including other-day recordings
  // sitting on this same row, since `more_audios` accumulates across dates.
  // A roster chart has no visit day of its own, so it always shows everything
  // on its row grouped by day.
  const groups = useMemo(() => {
    if (!visitDate || includeOtherDays) return allGroups;
    return allGroups.filter((g) => g.isThisVisit);
  }, [allGroups, visitDate, includeOtherDays]);

  const entries = useMemo(() => groups.flatMap((g) => g.entries), [groups]);
  const hasThisVisitGroup = allGroups.some((g) => g.isThisVisit);
  // Recordings on THIS row that belong to other days — the ones the filter
  // above hides. Counted so the hiding is stated, never silent.
  const otherDayOnThisRow = useMemo(
    () => (visitDate ? allGroups.filter((g) => !g.isThisVisit).reduce((n, g) => n + g.entries.length, 0) : 0),
    [allGroups, visitDate],
  );

  // Auto-select something playable so the modal doesn't open on an empty
  // detail pane — this visit's first recording when there is one, otherwise
  // the newest day's. Re-runs when the list changes (the cross-visit fetch
  // lands after the first paint).
  useEffect(() => {
    if (!isOpen) return;
    if (entries.length === 0) {
      setSelectedId(null);
      return;
    }
    const preferred = entries.find((e) => e.isThisVisit) || entries[0];
    setSelectedId((prev) => (prev && entries.some((e) => e.id === prev) ? prev : preferred.id));
  }, [isOpen, entries]);

  const selected = useMemo(
    () => entries.find((e) => e.id === selectedId) || null,
    [entries, selectedId],
  );

  const handleClose = useCallback(() => {
    setSelectedId(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const total = entries.length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        role="dialog"
        aria-label="Audios"
        className="bg-white dark:bg-[#14141c] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800/60 w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-extrabold text-base text-slate-900 dark:text-slate-50">🎧 Audios</div>
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 truncate">
              {patient?.patient_name || 'Patient'}
              {visitDate ? ` · visit ${visitDate}` : ''}
              {' · '}
              {total === 0 ? 'no recordings' : `${total} recording${total === 1 ? '' : 's'}`}
              {loadingOthers && ' · checking other days…'}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Only meaningful on a dated chart — a roster chart already shows
                every day it has. */}
            {visitDate && (
              <button
                type="button"
                onClick={() => setIncludeOtherDays((v) => !v)}
                aria-pressed={includeOtherDays}
                title={includeOtherDays
                  ? 'Showing this patient’s other days too — click to show only this visit'
                  : 'Showing only this visit’s recordings — click to also look at this patient’s other days'}
                className={[
                  'text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border whitespace-nowrap transition-colors',
                  includeOtherDays
                    ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200 border-indigo-300 dark:border-indigo-500/40'
                    : 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700/60 hover:bg-slate-200 dark:hover:bg-slate-800/70',
                ].join(' ')}
              >
                {includeOtherDays ? '◉' : '○'} Other days
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40 shrink-0"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Recording list */}
          <div className="md:w-72 md:shrink-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800/60 overflow-y-auto max-h-[38%] md:max-h-none">
            {total === 0 && !loadingOthers && (
              <p className="text-sm text-slate-600 dark:text-slate-300 p-4">
                {visitDate
                  ? `No recordings for this visit (${visitDate}).`
                  : 'No recordings found for this patient.'}
                {visitDate && !includeOtherDays && (
                  <>
                    {' '}
                    Use <span className="font-semibold">Other days</span> above to look
                    through the rest of this patient’s chart.
                  </>
                )}
              </p>
            )}
            {/* Never hide rows silently: if this row is carrying recordings
                filed under other days, say how many and how to see them. */}
            {visitDate && !includeOtherDays && otherDayOnThisRow > 0 && (
              <p className="text-[11px] text-slate-600 dark:text-slate-300 px-3 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-slate-200 dark:border-slate-800/60">
                {otherDayOnThisRow} more recording{otherDayOnThisRow === 1 ? '' : 's'} on this
                chart {otherDayOnThisRow === 1 ? 'is' : 'are'} filed under other days — turn on
                “Other days” to see {otherDayOnThisRow === 1 ? 'it' : 'them'}.
              </p>
            )}
            {/* Answers "why am I looking at another date?" before the user has
                to ask it — the toggle is on and this visit itself is empty. */}
            {visitDate && includeOtherDays && !hasThisVisitGroup && total > 0 && (
              <p className="text-[11px] text-slate-600 dark:text-slate-300 px-3 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-slate-200 dark:border-slate-800/60">
                Nothing was recorded on this visit ({visitDate}). The recordings below
                are from this patient’s other days.
              </p>
            )}
            {groups.map((group) => (
              <div key={group.key}>
                {/* Opaque, not /60: rows scrolling under a translucent sticky
                    header showed through it. */}
                <div className="sticky top-0 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700/60 flex items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {group.label}
                  </span>
                  {group.isThisVisit && (
                    <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-600 text-white">
                      This visit
                    </span>
                  )}
                </div>
                {group.entries.map((entry) => {
                  const active = entry.id === selectedId;
                  const when = formatWhen(audioMillis(entry.audio));
                  const dur = formatDuration(entry.audio?.duration);
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedId(entry.id)}
                      aria-current={active ? 'true' : undefined}
                      className={[
                        'w-full text-left px-3 py-2 border-b border-slate-100 dark:border-slate-800/40 flex items-start gap-2',
                        active
                          ? 'bg-indigo-50 dark:bg-indigo-500/15'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/40',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0',
                          active
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
                        ].join(' ')}
                      >
                        {entry.number}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                          {pickAudioDisplayName(entry.audio, entry.number)}
                        </span>
                        <span className="block text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {[when, dur].filter(Boolean).join(' · ') || 'date unknown'}
                        </span>
                      </span>
                      <span
                        title={entry.transcripts.length ? 'Has a transcript' : 'No transcript'}
                        aria-hidden="true"
                        className={[
                          'mt-1 w-2 h-2 rounded-full shrink-0',
                          entry.transcripts.length ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600',
                        ].join(' ')}
                      />
                    </button>
                  );
                })}
              </div>
            ))}
            {othersError && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300 p-3">
                Other visits couldn’t be loaded: {othersError}
              </p>
            )}
            {othersCapped && !othersError && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 p-3">
                Searched the {OTHER_CHART_LIMIT} most recent visits — older ones aren’t listed.
              </p>
            )}
          </div>

          {/* Player + transcript */}
          <div className="flex-1 min-w-0 overflow-y-auto p-4">
            {!selected && (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {total === 0
                  ? 'Nothing has been recorded on this visit yet. Recordings made from the Record Visit button show up here.'
                  : 'Pick a recording on the left to play it.'}
              </p>
            )}
            {selected && (
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-50 break-words">
                    {pickAudioDisplayName(selected.audio, selected.number)}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span
                      className={[
                        'text-[11px] font-semibold px-2 py-0.5 rounded-md',
                        selected.isThisVisit
                          ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200'
                          : 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200',
                      ].join(' ')}
                    >
                      {selected.isThisVisit ? `This visit · ${selected.groupLabel}` : selected.groupLabel}
                    </span>
                    {formatWhen(audioMillis(selected.audio)) && (
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        {formatWhen(audioMillis(selected.audio))}
                      </span>
                    )}
                    {formatDuration(selected.audio?.duration) && (
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        {formatDuration(selected.audio.duration)}
                      </span>
                    )}
                    {selected.audio?.source && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300">
                        {selected.audio.source}
                      </span>
                    )}
                  </div>
                </div>

                {selected.audio?.url ? (
                  // AudioPlayer (not a bare <audio>) because it re-signs
                  // Supabase Storage URLs — the public URL stored on the row
                  // 400s on a private bucket — and falls back to the backup
                  // bucket when the primary object is gone.
                  //
                  // The explicit text color is load-bearing: the player's own
                  // labels inherit it, which is how they stay readable here in
                  // dark mode. `overflow-x-auto` absorbs its fixed 500px width
                  // when the sidebar is dragged narrow.
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-800/40 text-slate-800 dark:text-slate-100 overflow-x-auto flex justify-center">
                    <AudioPlayer
                      key={selected.id}
                      audioUrl={selected.audio.url}
                      audio_duration={selected.audio.duration}
                      patientId={selected.rowId}
                      audioSavedAt={
                        selected.audio.uploaded_at
                        || selected.audio.recording_date
                        || selected.audio.created_at
                      }
                      audioDisplayName={pickAudioDisplayName(selected.audio, selected.number)}
                      tone={theme === 'dark' ? 'dark' : 'light'}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    This recording has no audio file attached — only its transcript was saved.
                  </p>
                )}

                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Transcript
                  </div>
                  {selected.transcripts.length === 0 ? (
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      No transcript saved for this recording.
                    </p>
                  ) : (
                    selected.transcripts.map((t, i) => (
                      <TranscriptBlock
                        key={t.id || t.session_id || i}
                        transcript={t}
                        index={i}
                        total={selected.transcripts.length}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer — hand-off to the full manager so this modal isn't a dead end
            when someone needs to rename, delete, or recover from backups. */}
        {onOpenAudioManager && (
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800/60 flex justify-end">
            <button
              type="button"
              onClick={() => { handleClose(); onOpenAudioManager(); }}
              className="text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:underline"
            >
              Rename, delete or recover backups in the Audio Manager →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
